import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { ZitaLogo } from '@/components/ZitaLogo';
import { NAV, AUTH } from '@/constants/testIds';
import { FEATURES } from '@/config/features';
import {
  Search, Briefcase, Users, Settings as SettingsIcon, CreditCard,
  LogOut, Menu, X, ShieldCheck, ArrowUpRight, Sparkles, Loader,
} from 'lucide-react';

/**
 * Aug 2026 §11 — Sidebar collapsed to 5 primary destinations.
 * Seek · Vault · Connect · Settings · Super Admin (only when elevated).
 *
 * All prior destinations remain reachable — Rules/Alerts/Collections/
 * Persistent Results/History are now tabs inside Vault. Team & Support
 * live inside Settings. Billing sits at the bottom.
 */

const NAV_ITEMS = [
  { to: '/seek', label: 'Seek', icon: Search, tid: NAV.seek },
  { to: '/vault', label: 'Vault', icon: Briefcase, tid: NAV.vault },
  { to: '/connect', label: 'Connect', icon: Users, tid: NAV.connect },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, tid: NAV.settings },
];

const REMODDASH_URL = 'https://remoddash.com';

const AdminUnreadBadge = () => {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user?.is_admin_active) return;
    let cancelled = false;
    const pull = () => {
      api.get('/admin/tickets/unread_count')
        .then((r) => { if (!cancelled) setUnread(r.data.unread_tickets || 0); })
        .catch(() => {});
    };
    pull();
    const id = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);
  if (!user?.is_admin_active || unread === 0) return null;
  return (
    <span
      data-testid="appshell-admin-unread-badge"
      className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-semibold"
    >
      {unread}
    </span>
  );
};

// Aug 2026 §7 — background scan pill.
// Polls /api/scans/active every 4s while at least one scan is running.
// Click routes to the newest active scan so the user can watch it complete.
const ActiveScansPill = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  useEffect(() => {
    if (!user) return () => {};
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await api.get('/scans/active');
        if (!cancelled) setActive(data?.active || []);
      } catch { /* ignore */ }
    };
    pull();
    const id = setInterval(pull, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);
  if (!active.length) return null;
  const latest = active[0];
  const label = active.length === 1
    ? (latest.search_term || latest.command_text || 'Scan').slice(0, 40)
    : `${active.length} scans running`;
  return (
    <button
      type="button"
      onClick={() => navigate(`/results/${latest.scan_id}`)}
      data-testid="appshell-active-scan-pill"
      className="flex items-center gap-2 w-full text-left px-3 py-2 mx-3 mb-2 rounded-full bg-ari/20 text-white text-xs font-medium hover:bg-ari/30 transition"
    >
      <Loader size={14} className="animate-spin shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      <span className="text-[10px] uppercase tracking-wider opacity-80">
        {latest.sources_done}/{latest.sources_total || '·'}
      </span>
    </button>
  );
};

const NavItems = ({ onClick }) => (
  <nav className="flex flex-col gap-1 px-3">
    {NAV_ITEMS.map(({ to, label, icon: Icon, tid }) => (
      <NavLink
        key={to}
        to={to}
        onClick={onClick}
        data-testid={tid}
        className={({ isActive }) => `group zita-nav-item ${isActive ? 'active' : ''}`}
      >
        <Icon size={20} strokeWidth={1.75} className="shrink-0 zita-nav-icon" />
        <span>{label}</span>
      </NavLink>
    ))}
  </nav>
);

const SidebarInner = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-full text-white">
      <Link
        to="/seek"
        onClick={onNavigate}
        data-testid={NAV.brand}
        className="flex items-center gap-2 pl-3 pr-4 pt-5 pb-6"
      >
        <ZitaLogo size={54} feathered variant="tight" />
        <span className="font-serif text-4xl text-white leading-none">Zita</span>
      </Link>
      <NavItems onClick={onNavigate} />
      <ActiveScansPill />
      <div className="mt-auto p-4 space-y-1">
        <NavLink
          to="/billing"
          onClick={onNavigate}
          data-testid={NAV.billing}
          className={({ isActive }) => `zita-nav-item ${isActive ? 'active' : ''} text-sm`}
        >
          <CreditCard size={16} className="zita-nav-icon" /> Billing
        </NavLink>
        {user?.is_admin_active && (
          <NavLink
            to="/admin"
            onClick={onNavigate}
            data-testid="nav-super-admin"
            className={({ isActive }) => `zita-nav-item ${isActive ? 'active' : ''} text-sm font-semibold`}
          >
            <ShieldCheck size={16} className="zita-nav-icon" />
            <span className="flex-1">Super Admin</span>
            <AdminUnreadBadge />
          </NavLink>
        )}
        {FEATURES.remoddashPromo && (
          <a
            href={REMODDASH_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            data-testid="nav-business-tools"
            className="zita-nav-item text-sm"
          >
            <ArrowUpRight size={16} className="zita-nav-icon remod-accent" />
            Business Tools · RemodDash
          </a>
        )}
        {user && (
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            data-testid={AUTH.logoutButton}
            className="zita-nav-item text-sm w-full"
          >
            <LogOut size={16} className="zita-nav-icon" /> Sign out
          </button>
        )}
        <div className="text-[11px] text-white/60 px-4 pt-3 leading-relaxed space-y-1">
          <div>Part of the <span className="remod-accent font-semibold">RemodWorks</span> AI Ecosystem</div>
          <div className="text-white/50">
            <Link to="/about" className="hover:text-white">About</Link>
            {' · '}
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            {' · '}
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <div className="text-white/50">
            <a href="mailto:support@zitaview.com" className="hover:text-white">
              support@zitaview.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AppShell = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="min-h-screen flex bg-transparent">
      <aside className="hidden md:flex md:w-[264px] lg:w-[280px] shrink-0">
        <div className="w-full sticky top-0 h-screen overflow-y-auto zita-scroll zita-sidebar">
          <SidebarInner />
        </div>
      </aside>

      <div
        className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3 zita-sidebar"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        data-testid="mobile-top-bar"
      >
        <Link to="/seek" className="flex items-center gap-2">
          <ZitaLogo size={32} feathered variant="tight" />
          <span className="font-serif text-2xl text-white leading-none">Zita</span>
        </Link>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="p-2 rounded-full text-white hover:bg-white/10"
          aria-label="Open menu"
          data-testid="mobile-drawer-open"
        >
          <Menu size={22} />
        </button>
      </div>

      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          data-testid="mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <div
            className="relative w-[82%] max-w-xs h-full max-h-screen zita-sidebar shadow-2xl flex flex-col"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="self-end p-3 text-white/80 hover:text-white shrink-0"
              aria-label="Close menu"
              data-testid="mobile-drawer-close"
            >
              <X size={22} />
            </button>
            <div className="flex-1 min-h-0 overflow-y-auto zita-scroll" data-testid="mobile-drawer-scroll">
              <SidebarInner onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 pt-16 md:pt-0">{children}</main>
      </div>
    </div>
  );
};
